import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Clock, CircleCheck, Fingerprint } from "lucide-react";
import { BillApprovalRow } from "./BillApprovalRow";
import { BillApproveDialog } from "./BillApproveDialog";
import { BillPayDialog } from "./BillPayDialog";
import { BillSignDialog } from "./BillSignDialog";
import { rejectBill } from "../../api/accounting";
import { useApiError } from "../../hooks/useApiError";

const CARD_3D = { boxShadow: "0 14px 28px rgba(0,0,0,0.22), 0 8px 12px rgba(0,0,0,0.14), inset 0 1.5px 0 rgba(255,255,255,0.22)" };

// 3 أقسام: بانتظار الموافقة → معتمد بانتظار الدفع → بانتظار توقيع المستلم.
export const BillsApprovalsView = ({ bills, boxes, employees = [], branchesById, suppliersById,
                                     accountsByCode = {}, onReload }) => {
  const { t } = useTranslation();
  const describe = useApiError();
  const [approving, setApproving] = useState(null);
  const [paying, setPaying] = useState(null);
  const [signing, setSigning] = useState(null);
  const pending  = bills.filter((b) => b.status === "pending_approval");
  const approved = bills.filter((b) => b.status === "approved");
  const awaiting = bills.filter((b) => b.status === "awaiting_delivery");

  const doReject = async (bill) => {
    const reason = window.prompt(t("acct.bills.approve.reason"), "");
    if (reason === null) return;
    try { await rejectBill(bill.id, reason || ""); toast(t("acct.bills.approve.rejected")); onReload(); }
    catch (e) { toast.error(describe(e)); }
  };

  const Section = ({ title, list, onClick, color, actionKey, Icon, allowReject, showShare }) => (
    <div className={`rounded-3xl border p-6 ${color}`} style={CARD_3D}>
      <div className="mb-3 flex items-center gap-2 text-primary">
        <Icon className="h-5 w-5" />
        <h3 className="text-base font-black">{title} ({list.length})</h3>
      </div>
      {list.length === 0
        ? <p className="py-8 text-center text-xs text-muted-foreground">{t("acct.bills.approve.empty")}</p>
        : list.map((b) => (
            <BillApprovalRow key={b.id} bill={b} onAction={onClick} actionKey={actionKey}
                             actionLabel={t(`acct.bills.approve.${actionKey}`)}
                             onReject={allowReject ? doReject : null}
                             showShare={showShare}
                             branchName={branchesById[b.branch_id]}
                             supplierName={suppliersById[b.supplier_id]}
                             accountName={accountsByCode[b.expense_account]} />
          ))}
    </div>
  );

  return (
    <div className="grid gap-4">
      <Section title={t("acct.bills.approve.pageTitle")} list={pending} onClick={setApproving}
               color="border-yellow-500/40 bg-yellow-500/5" actionKey="action" Icon={Clock} allowReject />
      <Section title={t("acct.bills.pay.pageTitle")} list={approved} onClick={setPaying}
               color="border-blue-500/40 bg-blue-500/5" actionKey="payAction" Icon={CircleCheck} allowReject />
      <Section title={t("acct.bills.sign.pageTitle")} list={awaiting} onClick={setSigning}
               color="border-sky-500/40 bg-sky-500/5" actionKey="signAction" Icon={Fingerprint} showShare />
      {approving ? <BillApproveDialog bill={approving}
                                       onCancel={() => setApproving(null)}
                                       onDone={() => { setApproving(null); onReload(); }} /> : null}
      {paying ? <BillPayDialog bill={paying} boxes={boxes} employees={employees}
                                onCancel={() => setPaying(null)}
                                onDone={() => { setPaying(null); onReload(); }} /> : null}
      {signing ? <BillSignDialog bill={signing} cashboxes={boxes}
                                  onCancel={() => setSigning(null)}
                                  onDone={() => { setSigning(null); onReload(); }} /> : null}
    </div>
  );
};
